import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconSend,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useContactForm } from '@/hooks/useContactForm';

const contactInfo = [
  {
    icon: IconPhone,
    title: 'Phone',
    value: '(651) 400-0063',
    href: 'tel:+16514000063',
  },
  {
    icon: IconMail,
    title: 'Email',
    value: 'info@hillsideharmony.com',
    href: 'mailto:info@hillsideharmony.com',
  },
  {
    icon: IconMapPin,
    title: 'Address',
    value: '135 Geranium Ave E, Saint Paul, MN 55117',
    href: 'https://maps.google.com/?q=135+Geranium+Ave+E,+Saint+Paul,+MN+55117',
  },
  {
    icon: IconClock,
    title: 'Tour Hours',
    value: 'Mon-Fri: 9am-5pm, Sat: 10am-3pm',
    href: null,
  },
];

const interestOptions = [
  { value: 'tour', label: 'Schedule a Tour' },
  { value: 'general', label: 'General Inquiry' },
  { value: 'services', label: 'Services Information' },
  { value: 'pricing', label: 'Pricing & Availability' },
  { value: 'careers', label: 'Career Opportunities' },
  { value: 'other', label: 'Other' },
];

export function ContactPage() {
  const { submitForm, isSubmitting } = useContactForm();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      interest: '',
      message: '',
    },
    validate: {
      name: (value) => (value.trim().length < 2 ? 'Name is required' : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      message: (value) => (value.trim().length < 10 ? 'Message must be at least 10 characters' : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    const result = await submitForm(values);
    if (result.success) {
      setSubmitted(true);
      form.reset();
      notifications.show({
        title: 'Message Sent!',
        message: 'Thank you for contacting us. We will get back to you soon.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } else {
      notifications.show({
        title: 'Error',
        message: result.error || 'Failed to send message. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  return (
    <>
      {/* Hero Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="md" maw={600} mx="auto">
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Contact Us
            </Text>
            <Title ta="center">
              We'd Love to Hear From You
            </Title>
            <Text c="dimmed" ta="center" size="lg">
              Whether you're interested in scheduling a tour, learning about our services,
              or just have questions, our team is here to help.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box py={80}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={50}>
            {/* Contact Form */}
            <Card padding="xl" shadow="sm" radius="lg">
              <Title order={3} mb="lg">
                Send Us a Message
              </Title>

              {submitted ? (
                <Alert
                  icon={<IconCheck size={16} />}
                  title="Thank you!"
                  color="green"
                  variant="light"
                >
                  Your message has been sent successfully. We'll get back to you within 24-48 hours.
                </Alert>
              ) : (
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Stack gap="md">
                    <TextInput
                      label="Your Name"
                      placeholder="John Doe"
                      required
                      {...form.getInputProps('name')}
                    />
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <TextInput
                        label="Email Address"
                        placeholder="john@example.com"
                        required
                        {...form.getInputProps('email')}
                      />
                      <TextInput
                        label="Phone Number"
                        placeholder="(555) 123-4567"
                        {...form.getInputProps('phone')}
                      />
                    </SimpleGrid>
                    <Select
                      label="I'm Interested In"
                      placeholder="Select an option"
                      data={interestOptions}
                      {...form.getInputProps('interest')}
                    />
                    <Textarea
                      label="Message"
                      placeholder="Tell us how we can help you..."
                      minRows={4}
                      required
                      {...form.getInputProps('message')}
                    />
                    <Button
                      type="submit"
                      size="lg"
                      leftSection={<IconSend size={18} />}
                      loading={isSubmitting}
                    >
                      Send Message
                    </Button>
                  </Stack>
                </form>
              )}
            </Card>

            {/* Contact Info */}
            <Stack gap="xl">
              <Box>
                <Title order={3} mb="lg">
                  Get In Touch
                </Title>
                <Text c="dimmed" mb="xl">
                  We're here to answer any questions you may have about our community,
                  services, or the admissions process. Reach out to us using any of
                  the methods below.
                </Text>
              </Box>

              <Stack gap="lg">
                {contactInfo.map((item) => (
                  <Group key={item.title} gap="md" wrap="nowrap">
                    <ThemeIcon size={50} radius="md" variant="light" color="harmony">
                      <item.icon size={24} stroke={1.5} />
                    </ThemeIcon>
                    <Box>
                      <Text fw={500}>{item.title}</Text>
                      {item.href ? (
                        <Text
                          component="a"
                          href={item.href}
                          c="harmony.6"
                          td="none"
                          style={{ cursor: 'pointer' }}
                        >
                          {item.value}
                        </Text>
                      ) : (
                        <Text c="dimmed">{item.value}</Text>
                      )}
                    </Box>
                  </Group>
                ))}
              </Stack>

              {/* Map Placeholder */}
              <Card
                padding={0}
                radius="lg"
                h={300}
                bg="gray.2"
                style={{ overflow: 'hidden' }}
              >
                <iframe
                  title="Location Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2821.137!2d-93.0593!3d44.9968!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b32d144b1e1d3b%3A0x8c9d0e1f2a3b4c5d!2s135%20Geranium%20Ave%20E%2C%20Saint%20Paul%2C%20MN%2055117!5e0!3m2!1sen!2sus!4v1648000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </Card>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={80} bg="gray.0">
        <Container size="md">
          <Stack align="center" gap="md" mb={50}>
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Common Questions
            </Text>
            <Title order={2} ta="center">
              Frequently Asked Questions
            </Title>
          </Stack>

          <Stack gap="md">
            {[
              {
                q: 'What are your visiting hours?',
                a: 'Families and friends are welcome to visit daily from 8am to 8pm. Extended hours can be arranged for special circumstances.',
              },
              {
                q: 'How do I schedule a tour?',
                a: 'You can schedule a tour by calling us, filling out the contact form above, or emailing us directly. Tours are available Monday through Saturday.',
              },
              {
                q: 'What should I bring for a tour?',
                a: 'Just bring yourself and any questions you have! We recommend allowing about an hour for a comprehensive tour and conversation.',
              },
              {
                q: 'Do you accept Medicare or Medicaid?',
                a: 'We accept various payment options. Please contact us to discuss your specific situation and we\'ll help you understand your options.',
              },
            ].map((faq, index) => (
              <Card key={index} padding="lg">
                <Text fw={600} mb="xs">{faq.q}</Text>
                <Text c="dimmed">{faq.a}</Text>
              </Card>
            ))}
          </Stack>
        </Container>
      </Box>
    </>
  );
}
